import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Dropdown,
  Avatar,
  Modal,
  message,
  Spin,
  Switch,
} from "antd";
import {
  UserOutlined,
  LogoutOutlined,
  LoginOutlined,
  DownOutlined,
  SafetyOutlined,
  EnvironmentOutlined,
  ToolOutlined,
} from "@ant-design/icons";
import type { MenuProps } from "antd";
import { useAuth } from "../../hooks/useAuth";
import { useUserRole } from "../../hooks/useUserRole";
import { useDevModeContext } from "@/contexts/DevModeContext";

// Only show the Dev mode toggle when a build explicitly opts in via
// VITE_DEV_MODE - only .env.development (DOGMA QA) ships it on; QA, UAT and
// production navbars never show this control.
const canShowDevModeToggle = import.meta.env.VITE_DEV_MODE !== "false";

interface AuthButtonProps {
  textColor?: string;
  backgroundColor?: string;
}

const AuthButton: React.FC<AuthButtonProps> = ({
  textColor = "#ffffff",
  backgroundColor = "transparent",
}) => {
  const navigate = useNavigate();
  const { isAuthenticated, user, loginRedirect, logoutRedirect, isLoading } =
    useAuth();
  const {
    role,
    region,
    updateRegion,
    isLoading: isRoleLoading,
  } = useUserRole();
  const [loggingOut, setLoggingOut] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { devMode, toggleDevMode } = useDevModeContext();

  // Helper function to check if region is empty
  const isRegionEmpty = (reg: string | null | undefined): boolean => {
    if (!reg) return true; // null, undefined, or empty string
    if (typeof reg === "string" && reg.trim() === "") return true; // whitespace only
    return false;
  };

  const handleLogin = async () => {
    try {
      await loginRedirect();
      // Note: Success message will be shown after redirect
    } catch (error) {
      console.error("Login failed:", error);
      message.error("Login failed. Please try again.");
    }
  };

  const handleLogout = () => {
    Modal.confirm({
      title: "Confirm Logout",
      content: "Are you sure you want to log out?",
      okText: "Yes, Logout",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          setLoggingOut(true);
          await logoutRedirect();
          // Note: User will be redirected, so no success message needed
        } catch (error) {
          console.error("Logout failed:", error);
          message.error("Logout failed. Please try again.");
        } finally {
          setLoggingOut(false);
        }
      },
    });
  };

  if (!isAuthenticated) {
    return (
      <Button
        type="primary"
        icon={<LoginOutlined />}
        onClick={handleLogin}
        loading={isLoading}
        size="large"
        style={{
          height: "40px",
          borderRadius: "6px",
          fontWeight: 500,
        }}
      >
        Sign In
      </Button>
    );
  }

  const handleRegionChange = (value: string) => {
    updateRegion(value);
    message.success(`Region updated to ${value}`);
    // Close dropdown after selection
    setTimeout(() => {
      setDropdownOpen(false);
    }, 300);
  };

  // Check if user has permission to edit region
  const canEditRegion = role === "Board Level" || role === "System Administrator";

  const menuItems: MenuProps["items"] = [
    {
      key: "user-info",
      label: (
        <div style={{ padding: "12px 8px" }}>
          <div
            style={{ fontWeight: 600, fontSize: "14px", marginBottom: "4px" }}
          >
            {user?.name || "User"}
          </div>
          <div
            style={{ fontSize: "12px", color: "#8c8c8c", marginBottom: "4px" }}
          >
            {user?.email || user?.username}
          </div>
          {isRoleLoading ? (
            <div
              style={{
                fontSize: "12px",
                color: "#8c8c8c",
                display: "flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <Spin size="small" />
              <span>Loading role...</span>
            </div>
          ) : role ? (
            <div
              style={{
                fontSize: "12px",
                color: "#1890ff",
                fontWeight: 500,
                display: "flex",
                alignItems: "center",
                gap: "4px",
                marginTop: "4px",
              }}
            >
              <SafetyOutlined />
              <span>{role}</span>
            </div>
          ) : (
            <div
              style={{
                fontSize: "12px",
                color: "#ff4d4f",
                fontWeight: 500,
                marginTop: "4px",
              }}
            >
              Role: {String(role)} (type: {typeof role})
            </div>
          )}
        </div>
      ),
      disabled: true,
    },
    {
      type: "divider" as const,
    },
    // Show region as read-only for all users
    ...(!isRegionEmpty(region)
      ? [
          {
            key: "region-info",
            label: (
              <div style={{ padding: "8px" }}>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#8c8c8c",
                    marginBottom: "8px",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  <EnvironmentOutlined />
                  <span>Region</span>
                </div>
                <div
                  style={{
                    padding: "8px 12px",
                    backgroundColor: "#f0f5ff",
                    borderRadius: "4px",
                    color: "#1890ff",
                    fontWeight: 500,
                    fontSize: "13px",
                  }}
                >
                  {region?.trim()}
                </div>
              </div>
            ),
            disabled: true,
          },
          {
            type: "divider" as const,
          },
        ]
      : []),
    ...(canShowDevModeToggle
      ? [
          {
            key: "dev-mode",
            label: (
              <div
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "12px",
                  padding: "4px 8px",
                }}
              >
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    fontSize: "13px",
                  }}
                >
                  <ToolOutlined />
                  Dev mode
                </span>
                <Switch size="small" checked={devMode} onChange={toggleDevMode} />
              </div>
            ),
          },
          {
            type: "divider" as const,
          },
        ]
      : []),
    {
      key: "logout",
      label: "Sign Out",
      icon: <LogoutOutlined />,
      danger: true,
      onClick: handleLogout,
    },
  ];

  return (
    <Dropdown
      menu={{ items: menuItems }}
      trigger={["click"]}
      placement="bottomRight"
      open={dropdownOpen}
      onOpenChange={setDropdownOpen}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "8px 16px",
          borderRadius: "8px",
          backgroundColor: isHovered
            ? "rgba(255, 255, 255, 0.2)"
            : backgroundColor,
          cursor: "pointer",
          transition: "all 0.3s ease",
          border: `1px solid ${
            isHovered ? "rgba(255, 255, 255, 0.3)" : "transparent"
          }`,
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Avatar
          icon={<UserOutlined />}
          style={{
            backgroundColor: "#1890ff",
            border: "2px solid rgba(255, 255, 255, 0.3)",
          }}
          size={36}
        />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
          }}
        >
          <span
            style={{
              color: textColor,
              fontWeight: 600,
              fontSize: "14px",
              lineHeight: "1.2",
            }}
          >
            {user?.name?.split(" ")[0] || "User"}
          </span>
          <span
            style={{
              color: textColor,
              fontSize: "12px",
              opacity: 0.8,
              lineHeight: "1.2",
            }}
          >
            {user?.email?.split("@")[0] || "user"}
          </span>
        </div>
        <DownOutlined
          style={{ color: textColor, fontSize: "10px", opacity: 0.7 }}
        />
      </div>
    </Dropdown>
  );
};

export default AuthButton;
